export const settings = {
  accountCompletionModal: {
    action: {
      complete: 'Complete',
      next: 'Next',
      save: 'Save',
    },
    description: 'Complete your details so you can start invoicing soon.',
    financialInfo: {
      description: 'Enter your account number below.',
      tab: 'Bank details',
    },
    frameworkAgreement: {
      error: 'Something went wrong while signing your agreement.',
      tab: 'Framework agreement',
    },
    peppol: {
      checkAgain: 'Check my Peppol inbox',
      description:
        '<paragraph>To send invoices through Creative Shelter, you need an <bold>active Peppol inbox</bold>. You don’t have one yet.</paragraph>\n<paragraph>Without a Peppol inbox, we can’t send your invoice. First activate your Peppol inbox with a recognized provider.</paragraph>\n<paragraph>As soon as your Peppol inbox is activated, we can find your Peppol ID automatically. Already taken care of that? Then you can have it checked again below.</paragraph>',
      tab: 'Peppol inbox',
    },
    successModal: {
      createFirstInvoice: 'Create your first invoice',
      description: 'You’re now ready to send your invoice or quotation!',
      title: 'Your account is ready',
    },
    title: 'Complete your account',
    vatRegulation: {
      error: 'Something went wrong while saving your VAT status.',
      tab: 'VAT status',
    },
  },
  alerts: {
    brands: {
      successCreated: 'Your trade name has been created.',
      successDeleted: 'Your trade name has been deleted.',
      successFavorite: 'Your favorite trade name has been changed.',
      successUpdated: 'Your trade name has been updated.',
    },
    community: {
      errorProfilePictureDeleted:
        'Something went wrong while deleting your Community profile picture. Contact service@creativeshelter.be if the problem persists.',
      errorProfilePictureTooLarge: 'The image you want to upload can’t be larger than 5 MB.',
      errorProfilePictureUploaded:
        'Something went wrong while uploading your Community profile picture. Contact service@creativeshelter.be if the problem persists.',
      errorUpdated:
        'Something went wrong while updating your Community profile. Contact service@creativeshelter.be if the problem persists.',
      successProfilePictureDeleted: 'Your Community profile picture has been deleted.',
      successProfilePictureUploaded: 'Your Community profile picture has been changed.',
    },
    customConditions: {
      successDeleted: 'Your custom terms and conditions have been deleted.',
    },
    errorActivities: 'Something went wrong while saving your activities.',
    errorPersonalInfo: 'Something went wrong while saving your profile information.',
    integrations: {
      successAccountantLinked: 'Your accountant has been added.',
      successAccountantUnlinked: 'Your accountant has been removed.',
      successBillitPaired: 'Your accounting software has been added.',
      successCashacaPaired: 'Cashaca has been connected.',
      successForwardingEmailLinked: 'The email address has been added.',
      successForwardingEmailUnlinked: 'The email address has been removed.',
    },
    invalidFields: 'Not everything has been filled in correctly. Check the highlighted fields.',
    numberingSeries: {
      duplicateFormat: 'You’ve already used the format of this numbering series. Could you choose a new one?',
      successCreated: 'Your numbering series has been created.',
      successDeleted: 'Your numbering series has been deleted.',
      successUpdated: 'Your numbering series has been updated.',
    },
    successUpdated: 'Your settings have been saved.',
    validFinancialInfo: 'Valid bank details',
  },
  breadcrumb: {
    documents: 'Documents',
    invitations: 'Invitations',
    pricing: 'Fee',
    root: 'Settings',
  },
  documents: {
    headers: {
      name: 'File name',
      type: 'Type',
    },
    title: 'Documents',
  },
  edit: 'Edit',
  fields: {
    accountantEmail: {
      label: 'Your accountant’s email address',
      placeholder: 'Email address',
    },
    activities: {
      other: {
        label: 'Enter your other creative activities here',
      },
      primary: {
        label: 'Main activity',
      },
      secondary: {
        label: 'Other activities',
      },
    },
    brandId: {
      label: 'Trade name',
    },
    brandName: {
      label: 'Trade name',
      placeholder: 'Your trade name',
    },
    city: {
      label: 'City',
    },
    color: {
      label: 'Color',
    },
    communityDescription: {
      label: 'Briefly introduce yourself and/or your activities',
    },
    communityEmail: {
      label: 'Public email address',
    },
    communityLinks: {
      label: 'Links',
      placeholders: {
        behance: 'https://www.behance.net/',
        facebook: 'https://www.facebook.com/',
        instagram: 'https://www.instagram.com/',
        linkedIn: 'https://www.linkedin.com/in/',
        website: 'https://',
      },
    },
    communityOpenToWork: {
      label: 'I’m available for assignments',
    },
    communityRegion: {
      label: 'Region',
      placeholder: 'Not specified',
    },
    companyName: {
      label: 'Company name',
    },
    currentPassword: {
      label: 'Current password',
    },
    email: {
      label: 'Email address',
      placeholder: 'Email address',
    },
    firstName: {
      label: 'First name',
      placeholder: 'First name',
    },
    forwarding: {
      alerts: {
        billtobox:
          'Could you add ‘administratie@creativeshelter.be’ as a known email address in Billtobox? Then everything will run smoothly right away!',
      },
      frequency: {
        label: 'Frequency',
        options: {
          instant: 'Per invoice or credit note (.pdf/.xml)',
          monthly: 'Per month (.zip)',
          quarterly: 'Per quarter (.zip)',
        },
        tooltip: 'If you entered accounting software, choose the option to forward a PDF and XML file per invoice or credit note.',
      },
      platformEmail: {
        label: 'Email address of your Accountable account',
        tooltip: 'Accountable uses this email address to recognize which account your invoices and credit notes belong to.',
      },
    },
    lang: {
      label: 'Language',
    },
    lastName: {
      label: 'Last name',
      placeholder: 'Last name',
    },
    logo: {
      error: {
        delete: 'Something went wrong while deleting the logo. Please try again later.',
        tooLarge: 'The logo you’re trying to upload can’t be larger than 5 MB.',
        upload: 'Something went wrong while uploading the logo.',
      },
      label: 'Trade name logo',
      tooltip:
        'If you select this trade name when preparing a document, this logo will be used instead of the logo of your main trade name.',
    },
    mainOccupation: {
      label: 'Your self-employed status',
      options: {
        '0': 'Secondary occupation',
        '1': 'Main occupation',
      },
    },
    newPassword: {
      label: 'New password',
    },
    newPasswordConfirmation: {
      label: 'Confirm new password',
    },
    number: {
      label: 'House number',
    },
    numberingSeries: {
      blocker: 'Are you sure you want to leave this page? Your changes to your numbering series have not been saved.',
      format: {
        amountOfDigits: {
          label: 'Number of digits',
          option: '{{amountOfDigits}} digits',
        },
        lengthWarning: 'We recommend limiting the length of the number to {{limit}} characters.',
        nextNumber: 'The next number in this series is <strong>{{number}}</strong>',
        prefixText: {
          label: 'Prefix',
        },
        separator: {
          label: 'Separator',
          none: 'None',
        },
        suffixText: {
          label: 'Suffix',
        },
        useSuffix: 'Use suffix',
        year: {
          label: 'Year/month',
          options: {
            YY: 'Short year ({{yearMonth}})',
            YYMM: 'Short year + month ({{yearMonth}})',
            YYYY: 'Year ({{yearMonth}})',
            YYYYMM: 'Year + month ({{yearMonth}})',
            none: 'None',
          },
        },
      },
      name: {
        label: 'Name',
        placeholder: 'E.g. Sales invoices',
        tooltip: 'Give your numbering series a name, so you can recognize it quickly.',
      },
      resetInterval: {
        description: 'Automatically reset the count to 1.',
        label: 'Restart count',
        options: {
          monthly: 'Monthly',
          never: 'Never',
          yearly: 'Yearly',
        },
      },
      startAt: {
        label: 'Starting number',
      },
      type: {
        label: 'Type',
        options: {
          both: 'Invoices and credit notes',
          credit_notes: 'Credit notes',
          invoices: 'Invoices',
        },
        tooltip:
          'In your accounting, there are two important types of documents: your invoices and your credit notes. You can use one numbering series for both, but splitting them is also possible. We leave that up to you.',
      },
    },
    phone: {
      label: 'Phone number',
      placeholder: '+32 499 99 99 99',
    },
    street: {
      label: 'Street',
    },
    vatID: {
      label: 'VAT number',
    },
    zipcode: {
      label: 'Postal code',
    },
  },
  general: {
    customConditions: {
      create: 'New terms and conditions',
      delete: {
        action: 'Delete custom terms and conditions',
        description: 'Are you sure you want to delete custom terms and conditions “{{name}}”?',
        title: 'Delete custom terms and conditions',
      },
      description: 'View the overview of all your custom terms and conditions here, or create new ones.',
      table: {
        actions: {
          creatingPdf: 'Generating PDF…',
          delete: 'Delete',
          view: 'View',
        },
        name: 'Name',
      },
      title: 'Custom terms and conditions',
      wizard: {
        already_agreement: {
          question: 'Have you already agreed with your client about how your work can be used?',
        },
        already_agreement_no: {
          question: 'No worries! With the following questions, you decide what your client can do with your work.',
        },
        already_agreement_no_paper: {
          question: 'No worries! With the following questions, we’ll put them on paper for you.',
        },
        already_agreement_paper: {
          question: 'Do you have those agreements on paper?',
        },
        already_agreement_yes_paper: {
          question:
            "Perfect, clear agreements make good friends. Indicate in the client’s profile that you already have an existing agreement and send it to us by email (<a href='mailto:administratie@creativeshelter.be'>administratie@creativeshelter.be</a>). We’ll gladly review the agreement for you and co-sign it as a third party.",
        },
        common_answers: {
          close: 'OK, close the wizard',
          next: 'OK, next question',
          no: 'No',
          ok: 'OK',
          previous: 'Previous question',
          start_questions: 'OK, start the questions',
          yes: 'Yes',
        },
        modify_how: {
          options: {
            '0': 'To be edited/changed by adding and/or removing elements.',
            '1': 'To be translated/adapted.',
          },
          question: 'How may your work be edited or changed?',
        },
        overview: {
          finish: 'OK, create my custom terms and conditions.',
          name: {
            label:
              'Enter the name of your new custom terms and conditions here. It can include the client’s name or the specific arrangements.',
            placeholder: 'For example ‘Right of use Benelux 1 year’',
          },
          question: 'Thanks, those were all the questions! Here’s an overview of your answers:',
        },
        reproduce_how: {
          options: {
            '0': 'In any way, in any form, with all techniques and on all media.',
            '1': 'In a specific form.',
            '2': 'In electronic form on a physical storage medium.',
            '3': 'In electronic form on an online medium.',
          },
          other_placeholder: 'Could you specify in which form?',
          question: 'In what way may your work be reproduced?',
        },
        reproduce_how_many: {
          options: {
            '0': 'Unlimited',
            '1': 'Limited',
          },
          other_placeholder: 'Specify the number',
          question: 'In how many copies may your work be reproduced?',
        },
        share_how: {
          options: {
            '0': 'Publish in …',
            '1': 'Make available via …',
            '2': 'Include in a book, catalog, …',
            '3': 'As promotional material and for commercial purposes.',
          },
          other_placeholder: 'Specify',
          question: 'In what way may your work be communicated to the public?',
        },
        start: {
          description: 'Follow the wizard below to put together your own custom terms and conditions.',
          ok: 'OK, start the wizard',
          title: 'Create your custom terms and conditions',
        },
        thanks: {
          close: 'Close the wizard',
          explanation:
            'Create your invoice and select the name of your custom T&Cs at the bottom of the ‘copyright agreement’ menu. Your custom T&Cs are automatically included under article 8 (8.1 to 8.3) in the appendix we send to your client together with our invoice. The special conditions field on our invoice to your client also refers to your custom T&Cs.',
          question:
            'Your custom terms and conditions are now being created for you. You’ll soon be able to view them and use them when creating your invoice!',
          subtitle: 'How do I get started?',
        },
        transfer_copyright: {
          question: 'Do you want to transfer the copyright on your work to your client?',
        },
        transfer_copyright_no: {
          question: 'OK, great! Then you need to indicate what may be done with your work.',
        },
        transfer_copyright_yes: {
          question: 'OK, perfect! Then choose ‘transfer’ when creating a quotation or invoice.',
        },
        use_how: {
          options: {
            '0': 'Make reproductions',
            '1': 'Communicate to the public',
            '2': 'Edit/change',
          },
          question: 'What may your client do with your work?',
        },
        use_how_long: {
          options: {
            '0': 'Full duration',
            '1': 'Other period',
          },
          other_placeholder: 'Specify the period',
          question: 'How long may your client use your work?',
        },
        use_where: {
          options: {
            '0': 'Belgium',
            '1': 'Europe',
            '2': 'The whole world',
            '3': 'Other',
          },
          other_placeholder: 'For example Benelux',
          question: 'Where may your client use your work?',
        },
      },
    },
    description: 'Change the layout and behavior of your platform.',
    email: {
      bcc: {
        description:
          'This means you’re in bcc on all emails we send to your clients, from new quotations, invoices and credit notes to invoice payment reminders.',
        title: 'Put me in bcc on all emails to my clients',
      },
      description: 'Choose which emails and notifications you want to receive',
      followUpEnabled: {
        description: 'Get a notification when there’s an update on the payment follow-up of an overdue invoice',
        title: 'Payment follow-up updates',
      },
      invitationReminder: {
        description: 'Get a reminder when the fellow freelancer you invited in turn gets a reminder to register.',
        title: 'Reminder about sent invitations',
      },
      jobPostUpdates: {
        description: 'Get a weekly overview of new (and not yet filled) assignments based on your main and other activities.',
        title: 'Weekly overview of new assignments',
      },
      nettoSaved: {
        description:
          'Get an overview of what you save each month by using Creative Shelter. That way you see in black and white how much extra you earn by cashing in on your copyright.',
        title: 'Monthly net savings email',
      },
      title: 'Emails & notifications',
    },
    numberingSeries: {
      add: 'New numbering series',
      delete: {
        description: 'Are you sure you want to delete “{{numberingSeries}}”?',
        title: 'Delete numbering series',
      },
      description:
        'System for automatically numbering your invoices and credit notes. Questions or want more info? <a href="https://knowledge.creativeshelter.be/wat-is-een-nummeringreeks-en-hoe-stel-ik-deze-in" target="_blank">You’ll find it here</a>.',
      edit: {
        format: 'Change format/options',
        name: 'Change name/type',
      },
      headers: {
        name: 'Name',
        nextNumber: 'Next number',
        type: 'Type',
      },
      title: 'Numbering series',
    },
    themeInvoice: {
      colorTheme: {
        helpText: 'Choose a color combination for the documents you send to your clients.',
        subtitle: 'Color theme',
      },
      logo: {
        helpText: 'Upload the logo of your sole proprietorship here.',
      },
      title: 'Invoice theme',
    },
    title: 'Settings',
  },
  header: {
    save: 'Save changes',
    title: 'Settings',
  },
  imageUpload: {
    logo: {
      delete: 'Delete logo',
      label: 'Logo',
      upload: 'Upload logo',
    },
    profilePicture: {
      delete: 'Delete photo',
      label: 'Profile picture',
      upload: 'Upload photo',
    },
  },
  integrations: {
    accountant: {
      current: 'The accountant below has been added to your account:',
      description: 'By adding your accountant, they get access to your invoices and documents through Creative Shelter’s Credit platform.',
      pair: 'Add',
      requested: 'The accountant below has requested access to your account:',
      title: 'Give your accountant access',
      unpair: 'Remove',
    },
    description: 'Link your Creative Shelter account with external services here.',
    externalSystems: {
      createAccount: 'Create account',
      description:
        'Connect your accounting software here to forward invoices and credit notes automatically. Note: this is not a connection with your Peppol inbox.',
      pairWith: 'Connect with {{ system }}',
      paired: 'Connected',
      peppolWarning:
        'Invoices and credit notes sent via Peppol may arrive twice through your connection. Check the settings of your accounting software to avoid processing them twice.',
      systems: {
        billit: {
          description:
            'Do you sometimes create non-creative invoices besides your creative ones? This integration lets you easily create those non-creative invoices and connect them to your Creative Shelter account.',
          name: 'Billit',
        },
        cashaca: {
          description: 'Cashaca simplifies your accounting. Ideal for self-employed people with a turnover of less than €500,000.',
          modal: {
            apiToken: 'Cashaca API code',
            description:
              'Enter your personal Cashaca API code here.<br/>Don’t have a Cashaca account yet? <a href="https://app.cashaca.be/gebruikers/registreer?utm_source=creativeshelter" target="_blank">Register here</a>.',
          },
          name: 'Cashaca',
        },
        dexxter: {
          alerts: {
            error: 'Something went wrong while connecting your Dexxter account.',
            success: 'You’re connected to your Dexxter account.',
          },
          description:
            'Your invoices and credit notes, automatically in Dexxter. A platform for doing your own accounting as a sole proprietor, right at your fingertips.',
          modal: {
            description:
              'Forward your invoices and credit notes to Dexxter automatically in two quick steps:<ol><li>Go to your settings and click ‘<dexxterLink href="https://go.dexxter.be/user-settings/general/company-settings">General</dexxterLink>’. Tick the ‘Activate copyright’ option and save.</li><li>Go to ‘Income’ and click ‘<dexxterLink href="https://go.dexxter.be/income-documents">Inbox</dexxterLink>’. In the inbox, click ‘View delivery method’ and copy the email address of your digital mailbox. Paste it below.</li></ol>',
            emailAddress: {
              placeholder: 'income.xxxxxxxxxxxxxxxx',
              suffix: '@inbound.dexxter.be',
              validatePrefix: 'The email address must start with ‘income.’.',
              validateSuffix: 'Only enter what comes before the ‘@’.',
            },
            title: 'Connect with Dexxter',
            titleTooltip:
              'Read more <faqLink href="https://www.creativeshelter.be/knowledge-hub/hoe-stuur-ik-mijn-facturen-automatisch-door-naar-dexxter">in our FAQ</faqLink>.',
          },
          name: 'Dexxter',
          tag: 'Get a €25 discount',
        },
      },
      title: 'Connect to external systems',
    },
    forwarding: {
      add: 'Add recipient',
      blocker: 'Are you sure you want to leave this page? Your changes to your forwarding address have not been saved.',
      deleteModal: {
        confirm: 'Yes, I’m sure.',
        description: 'Are you sure you no longer want to forward your invoices and credit notes to {{email}}?',
        dexxter: {
          confirm: 'Remove connection',
          description: 'Are you sure you no longer want to automatically forward your invoices and credit notes to Dexxter?',
          title: 'Remove Dexxter connection',
        },
        title: 'Stop automatic forwarding',
      },
      description:
        'Forward your invoices and credit notes automatically to your accountant or accounting software. <a href="https://www.creativeshelter.be/knowledge-hub/kan-ik-mijn-documenten-automatisch-doorsturen-naar-mijn-accountant" target="_blank">We explain it here.</a>',
      peppol: {
        description:
          'You receive all your invoices in your Peppol inbox. Forwarding them automatically or connecting an external system is no longer necessary, unless specifically requested.',
        title: 'Peppol is activated',
      },
      title: 'Automatic forwarding',
    },
    pair: 'Connect',
    peppol: {
      actions: {
        checkAgain: 'Check again',
      },
      confirmExtraIntegration: {
        cancel: 'Don’t connect',
        confirm: 'Connect anyway',
        description:
          'You already receive your invoices in your Peppol inbox. An extra connection usually isn’t necessary, unless your accountant or accounting software specifically asks for it. Are you sure you want to continue?',
        title: 'Extra connection needed?',
      },
      description: 'It’s the structured way to send and receive documents. And above all: mandatory since 2026, for you too.',
      disclaimerSelfBillingUnsupported:
        '<bold>From 1 January 2026, we send your invoices via Peppol, so you’re legally compliant.</bold><br>\nYour Peppol inbox doesn’t support <strong>self-billing</strong> (yet). As a result, your invoices may not end up in your accounting automatically yet. <strong>As an alternative</strong>, you can use our <strong>‘automatic forwarding’ setting</strong> to receive your invoices in your accounting automatically.',
      fields: {
        peppolId: {
          empty: 'No Peppol ID yet',
          label: 'Peppol ID',
          tooltip:
            'We found this Peppol ID for you. Not correct? <email href="mailto:service@creativeshelter.be">Email us</email> and we’ll change it for you.',
        },
        status: {
          label: 'Status',
          options: {
            Active: 'Activated',
            ActiveSelfBillingFallback: 'Self-billing not activated',
            NotRegistered: 'No Peppol ID found',
          },
        },
      },
      title: 'Peppol',
    },
    processing: 'We’re retrieving your data',
    title: 'Integrations',
    unpair: 'Disconnect',
  },
  nps: {
    ariaLabelTextarea: 'Write your opinion here',
    eyebrow: 'Your opinion counts',
    placeholderTextarea: 'Write your opinion here…',
    sendFeedback: 'Send feedback',
    titleStepOne: 'How likely are you to recommend Creative Shelter to other freelancers?',
    titleStepThree: 'Thanks! We’ll get to work with your feedback!',
  },
  profile: {
    activities: {
      title: 'Activities',
      warnings: {
        stylingAndInterior: {
          body: 'Since 2024, not all creative styling and interior services are still eligible for copyright income. The interpretation of the law is stricter than the law itself. Not sure whether your service is eligible? <a href="mailto:administratie@creativeshelter.be?subject=Komen mijn prestaties voor styling/interieur nog in aanmerking?" target="_blank">Email us</a> and we’ll take it from there with you. You’re still eligible for other creative activities.',
          title: 'Important! Stylist or interior designer? Then read on.',
        },
      },
    },
    brands: {
      add: 'Add trade name',
      delete: {
        description: 'Are you sure you want to delete “{{brand}}”?',
        title: 'Delete trade name',
      },
      description: 'View, create and edit your trade names.',
      edit: 'Edit trade name',
      favorite: {
        markAsFavorite: 'Mark as favorite',
        removeFavorite: 'Remove as favorite',
      },
      headers: {
        color: 'Trade name color',
        email: 'Email address',
        logo: 'Logo',
        name: 'Your trade name',
      },
      logoAdd: 'Add logo',
      logoEdit: 'Edit logo',
      mainBrand: 'Main trade name',
      title: 'Trade names',
    },
    community: {
      communityHidden:
        'Access to the Community has been disabled for you. For more info, you can always <a href="mailto:admin@creativeshelter.be?subject=Geblokkeerde toegang Community" target="_blank">send us an email</a>.',
      description: 'Edit your Community profile here',
      title: 'Community',
      visibleInCommunity: 'Visible in Community',
    },
    financial: {
      bank: 'Bank name',
      bic: 'BIC/Swift',
      check: 'Check',
      error: {
        foreignIban: 'Something went wrong while retrieving your bank details. Could you fill them in yourself?',
        general: 'Something went wrong while saving your account number.',
      },
      iban: 'Account number',
      title: 'Bank details',
    },
    info: {
      companyTitle: 'Your sole proprietorship details',
      kboDescription: 'These details come from the CBE database.',
      language: {
        en: 'English',
        fr: 'French',
        nl: 'Dutch',
      },
      refreshKboInformation: {
        action: 'Refresh details',
        lastUpdated: 'Updated {{timeAgo}} from the CBE',
        tooltip:
          'We retrieve the details directly from the <kboLink>Crossroads Bank for Enterprises</kboLink> (CBE for short). Are they no longer up to date? Then you can retrieve the latest details here. Only possible if the details are more than 7 days old.',
        unavailable_one: 'You can refresh your details again in 1 day.',
        unavailable_other: 'You can refresh your details again in {{count}} days.',
      },
      subsections: {
        kbo: 'Company details',
        personal: 'Personal information',
      },
      title: 'Profile information',
    },
    password: {
      edit: 'Change password',
      title: 'Change password',
    },
    title: 'Profile',
    vatRegulation: {
      description: 'Here you’ll find extra information about your current VAT status.',
      edit: 'Change',
      help: 'Indicate which VAT status you use. This determines whether VAT is charged on your invoices. Small businesses with a turnover below €25,000 can use the standard VAT exemption scheme. You can apply for this yourself. You can find more information <a href="https://knowledge.creativeshelter.be/wat-is-de-btw-vrijstellingsregeling-en-bestaan-er-nog-andere-regelingen" target="_blank">here</a>.',
      info: {
        none: 'You haven’t indicated your VAT status yet.',
        vatExempt:
          'You use the <strong>standard VAT exemption scheme</strong>. Invoices between you and Creative Shelter are issued without VAT. Keep track below of how far you are from the limit.',
        vatNormal: 'You use the <strong>regular VAT status</strong>. Invoices between you and Creative Shelter are issued with VAT.',
      },
      limit: 'Exemption scheme limit {{year}}',
      options: {
        vatExempt: {
          description: 'Small businesses with a turnover below €25,000',
        },
        vatNormal: {
          description: 'Businesses with a turnover of more than €25,000',
        },
      },
      title: 'VAT status',
      toggle: {
        vatExempt: 'VAT exempt',
        vatNormal: 'Subject to VAT',
      },
      toggleDescription: {
        vatExempt: 'Small businesses with a turnover below €25,000',
        vatNormal: 'Businesses with a turnover of more than €25,000',
      },
      tooltip: 'If your turnover is higher than €25,000, you can no longer use the standard VAT exemption scheme.',
      totalRevenueInYear: 'Total turnover {{ year }}',
      yearSelector: {
        ariaLabel: 'Select the year for which you want to view the turnover limit.',
      },
    },
  },
  prompts: {
    verifyAddress: {
      change: {
        actions: {
          back: 'Back',
          mail: 'Email us',
        },
        description: 'Moved? Let us know! Send an email with your latest address details and we’ll update your profile.',
        kboMessage:
          'Tip! Also pass on your latest address details to the Crossroads Bank for Enterprises (via <myEnterpriseLink>My Enterprise</myEnterpriseLink>).',
        mailLink: 'mailto:service@creativeshelter.be?subject=I have a new address&body=Hey%0D%0A%0D%0AMy new address is',
        title: 'Please change my address!',
      },
      verify: {
        actions: {
          confirm: 'Yes, correct!',
          edit: 'Not correct yet, please change it!',
          refresh: 'Retrieve details again',
        },
        description: 'The details below are filled in on your profile. Could you double-check your address?',
        errors: {
          refreshKboFailed: 'Your details can’t be retrieved from the Crossroads Bank for Enterprises right now.',
        },
        title: 'Double-check your address',
      },
    },
  },
  tabs: {
    company: 'Company details',
    email: 'Notifications',
    integrations: 'Integrations',
    invoice: 'Invoice settings',
    profile: 'Profile',
  },
  unsavedChanges: {
    description: 'Save your changes before you continue, or discard them to continue without saving.',
    discard: 'Discard changes',
    save: 'Save changes',
    title: 'You have unsaved changes',
  },
};
